import { useState } from 'react';
import { Icon } from '../ui/Icon';
import { Badge, Button, Card } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { RESULT_NOT_ACCOUNTING } from '../../utils/inicioView';
import type { ResultView } from '../../utils/inicioView';
import { RESULT_INDEPENDENT, WHY_DIFFERENT, WHY_DIFFERENT_TITLE, type BusinessReportView, type ProportionView } from '../../utils/reportsView';
import { DeltaText, ReportsMoney, ReportsNotes, ReportsSay, ReportsTile, Sentence } from './ReportsParts';

const BREAKDOWN_ID = 'reports-breakdown';

/**
 * Rediseño · Bloque F — R1 · «¿Cómo va mi negocio?» (P-030 `reportView`), con las cifras de
 * `owner-overview`: Entró · Salió · Te quedó del periodo, su comparación y, aparte, el resultado estimado
 * con su sobre (B0-01). Solo dibuja `BusinessReportView`.
 *
 * Lo que P-030 dibuja y este contrato no tiene no se dibuja: «Entró» por método de pago, las compras por
 * categoría, la flecha de «Salió», «¢ de cada peso» y «Ver las ventas».
 */
export function ReportBusiness({ view, onNavigate }: { view: BusinessReportView; onNavigate: (destination: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ReportsSay segments={view.say} />

      <Card aria-labelledby="reports-flows-title" as="section">
        <h2 className="n3-visually-hidden" id="reports-flows-title">Entró, salió y te quedó</h2>
        <ul className="n3-reports__flows">
          {view.flows.map((flow) => (
            <li className={cx('n3-reports__flow', `n3-reports__flow--${flow.key}`)} key={flow.key}>
              <p className="n3-reports__flow-label">
                <span aria-hidden="true" className="n3-reports__flow-icon"><Icon name={flow.icon} size={15} /></span>
                <span>{flow.label}</span>
              </p>
              <p className="n3-reports__flow-amount"><ReportsMoney label={flow.amountLabel} /></p>
              {flow.note && <p className="n3-reports__flow-note">{flow.note}</p>}
              {flow.delta && <DeltaText className="n3-reports__flow-delta" delta={flow.delta} />}
            </li>
          ))}
        </ul>
        {view.comparison && <p className="n3-reports__comparison">{view.comparison}</p>}
        {view.proportion && <Proportion view={view.proportion} />}
        <Button
          aria-controls={BREAKDOWN_ID}
          aria-expanded={open}
          className="n3-reports__why"
          iconStart={<Icon name={open ? 'chevron-up' : 'search'} size={16} />}
          onClick={() => setOpen((value) => !value)}
          variant="tonal"
        >
          {open ? 'Ocultar detalle' : 'Ver de dónde salió'}
        </Button>
        {open && (
          <div className="n3-reports__breakdown" id={BREAKDOWN_ID}>
            <section aria-labelledby="reports-in-title" className="n3-reports__panel">
              <h3 className="n3-reports__panel-title" id="reports-in-title">
                <span aria-hidden="true" className="n3-reports__panel-icon n3-reports__panel-icon--in"><Icon name="arrow-in" size={16} /></span>
                <span>Entró · tus ventas</span>
              </h3>
              <dl className="n3-reports__rows">
                <div className="n3-reports__row n3-reports__row--total"><dt>Total</dt><dd>{view.inflow.totalLabel}</dd></div>
              </dl>
              <p className="n3-reports__panel-note">{view.inflow.note}</p>
            </section>
            <section aria-labelledby="reports-out-title" className="n3-reports__panel">
              <h3 className="n3-reports__panel-title" id="reports-out-title">
                <span aria-hidden="true" className="n3-reports__panel-icon n3-reports__panel-icon--out"><Icon name="arrow-out" size={16} /></span>
                <span>Salió · compras y gastos confirmados</span>
              </h3>
              <dl className="n3-reports__rows">
                <div className="n3-reports__row"><dt>Compras de insumos</dt><dd>{view.outflow.purchasesLabel}</dd></div>
                <div className="n3-reports__row n3-reports__row--group"><dt>Gastos</dt><dd>{view.outflow.expensesLabel}</dd></div>
                {view.outflow.expenses.map((row) => (
                  <div className="n3-reports__row n3-reports__row--sub" key={row.name}><dt>{row.name}</dt><dd>{row.amountLabel}</dd></div>
                ))}
                <div className="n3-reports__row n3-reports__row--total"><dt>Total</dt><dd>{view.outflow.totalLabel}</dd></div>
              </dl>
              {view.outflow.noExpensesNote && <p className="n3-reports__panel-note">{view.outflow.noExpensesNote}</p>}
            </section>
          </div>
        )}
      </Card>

      <ReportsNotes notes={[view.disclaimer, view.notProfit]} />

      <h2 className="n3-reports__section-title" id="reports-estimate-title">Otra forma de verlo</h2>
      <Card aria-labelledby="reports-estimate-title" as="section" className="n3-reports__estimate">
        <ResultPanel onNavigate={onNavigate} view={view.result} />
        <div className="n3-reports__explain">
          <h3 className="n3-reports__explain-title"><Icon name="info" size={16} /><span>{WHY_DIFFERENT_TITLE}</span></h3>
          {WHY_DIFFERENT.map((paragraph, index) => <p className="n3-reports__explain-text" key={index}><Sentence segments={paragraph} /></p>)}
        </div>
      </Card>
    </>
  );
}

/**
 * La barra de P-030: de lo que entró, la parte que salió y la que quedó. Es dibujo, oculto al lector de
 * pantalla —las cifras ya están arriba, con palabras—, y su leyenda solo nombra los colores: ninguna
 * proporción se escribe.
 */
function Proportion({ view }: { view: ProportionView }) {
  return (
    <div aria-hidden="true" className="n3-reports__proportion">
      <div className="n3-reports__proportion-track">
        {view.outPercent > 0 && <span className="n3-reports__proportion-out" style={{ width: `${view.outPercent}%` }} />}
        {view.keptPercent > 0 && <span className="n3-reports__proportion-kept" style={{ width: `${view.keptPercent}%` }} />}
      </div>
      <p className="n3-reports__legend">
        <span className="n3-reports__legend-item"><span className="n3-reports__swatch n3-reports__swatch--out" />Salió</span>
        {!view.overflow && <span className="n3-reports__legend-item"><span className="n3-reports__swatch n3-reports__swatch--kept" />Te quedó</span>}
      </p>
    </div>
  );
}

/**
 * El resultado estimado (B0-01), con la presentación del Inicio y el periodo del reporte: confiable y
 * estimado son la cifra, con su cuenta en renglones; incompleto, un aviso con la cifra en chico; y no
 * calculable, el aviso SIN ninguna cifra, con lo que falta y el atajo que propone el servidor.
 */
function ResultPanel({ view, onNavigate }: { view: ResultView; onNavigate: (destination: string) => void }) {
  if (!view.isHeadline) {
    return (
      <div className="n3-reports__result n3-reports__result--warning">
        {/* La etiqueta va con el color del texto: el durazno sobre durazno da 3.21 : 1 (D-UI-PASTEL-CONTRAST). El icono lleva el aviso. */}
        <p className="n3-reports__result-label"><span aria-hidden="true" className="n3-reports__warning-icon"><Icon name="info" size={16} /></span><span>{view.label} · {view.periodLabel}</span></p>
        {view.amountLabel && <p className="n3-reports__result-small">{view.amountLabel} aprox.</p>}
        {view.message && <p className="n3-reports__result-message">{view.message}</p>}
        {/* P-072: parte del costo es aproximado (lo dice el servidor); misma zona explicativa, sin rediseñar. */}
        {view.approximateNote && <p className="n3-reports__result-coverage n3-reports__approximate-note">{view.approximateNote}</p>}
        {view.coverageText && <p className="n3-reports__result-coverage">{view.coverageText}</p>}
        <p className="n3-reports__result-note">{RESULT_INDEPENDENT}</p>
        {view.cta && <Button className="n3-reports__result-cta" onClick={() => onNavigate(view.cta!.destination)} variant="primary">{view.cta.label}</Button>}
      </div>
    );
  }
  return (
    <div className="n3-reports__result">
      <p className="n3-reports__result-label">
        <ReportsTile icon="coins" size="sm" tone="highlight" />
        <span>{view.label}</span>
      </p>
      <p className="n3-reports__result-amount"><ReportsMoney label={view.amountLabel!} /> <span className="n3-reports__approx">aprox.</span></p>
      <p className="n3-reports__result-period">{view.periodLabel}</p>
      {view.badge && <p className="n3-reports__result-estimate"><Badge tone="info">{view.badge}</Badge><span>{view.message}</span></p>}
      {view.approximateNote && <p className="n3-reports__result-coverage n3-reports__approximate-note">{view.approximateNote}</p>}
      {view.coverageText && <p className="n3-reports__result-coverage">{view.coverageText}</p>}
      {view.breakdown && (
        <dl className="n3-reports__rows n3-reports__rows--result">
          {view.breakdown.map((row) => (
            <div className={cx('n3-reports__row', row.isTotal && 'n3-reports__row--total')} key={row.label}><dt>{row.label}</dt><dd>{row.amountLabel}</dd></div>
          ))}
        </dl>
      )}
      {/* Con «Estimación», su supuesto ya va junto a la etiqueta: no se repite al pie. */}
      {view.breakdownNote && !view.badge && <p className="n3-reports__result-note">{view.breakdownNote}</p>}
      <p className="n3-reports__result-note">{RESULT_NOT_ACCOUNTING}</p>
    </div>
  );
}
